import React from "react";
import { FaUtensils, FaTimes, FaCheckCircle, FaCalendarDay, FaClock, FaClipboardList } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { APP_ROUTES } from "../../PageRouting/approutes";

/**
 * DietNotificationModal
 * 
 * Displays diet compliance notifications received via Firestore listeners.
 * 
 * @param {object[]} notifications - Array of diet notification objects
 * @param {function} onClose       - (id: string) => void
 */
export default function DietNotificationModal({ notifications, onClose }) {
  const history = useHistory();
  if (!notifications || notifications.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-6 z-[10000] flex flex-col gap-3 pointer-events-none max-w-[320px] w-full">
      {notifications.map((notif, index) => (
        <NotificationItem
          key={notif.id || index}
          notif={notif}
          onClose={onClose}
        />
      ))}

      {/* Custom Keyframes */}
      <style>{`
        @keyframes diet-slide-up {
          from { 
            opacity: 0; 
            transform: translateY(40px) scale(0.9) rotate(-2deg); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1) rotate(0); 
          }
        }
        .animate-diet-slide-up {
          animation: diet-slide-up 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>
    </div>
  );
}

function NotificationItem({ notif, onClose }) {
  const history = useHistory();
  // Auto-dismiss after 5 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose(notif.id);
    }, 5000);
    return () => clearTimeout(timer);
  }, [notif.id, onClose]);

  const handleReview = () => {
    // Navigate to the diet plan page (patient list) and pass the plan ID
    const planId = notif.dietPlanId || notif.planId || notif.id;
    history.push(APP_ROUTES.DIETPLAN, { autoOpenId: planId });
    onClose(notif.id);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Just now";
    try {
      const date = timestamp.seconds
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return "Just now";
    }
  };

  return (
    <div className="pointer-events-auto group relative overflow-hidden rounded-2xl bg-white/80 backdrop-blur-xl border border-white/50 shadow-[0_15px_35px_rgba(0,0,0,0.08)] transition-all duration-500 hover:shadow-[0_20px_45px_rgba(0,0,0,0.12)] hover:-translate-y-1 animate-diet-slide-up">
      {/* Top colored accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[#10b981] to-[#34d399]"></div>

      <div className="p-4" onClick={handleReview}
      >
        <div className="flex items-start gap-3">
          {/* Icon Container - Scaled Down */}
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 flex items-center justify-center text-emerald-600 shadow-inner border border-emerald-100/50">
              <FaUtensils className="text-xl" />
            </div>
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow border border-emerald-50">
              <FaCheckCircle className="text-emerald-500 text-[8px]" />
            </div>
          </div>

          {/* Information Section */}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-center mb-1">
              <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100/40 px-2 py-0.5 rounded-md border border-emerald-200/20">
                {notif.compliance || "Taken"}
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-bold text-gray-400">
                  {formatTimestamp(notif.createdAt)}
                </span>
                <button
                  onClick={() => onClose(notif.id)}
                  className="text-gray-300 hover:text-red-500 transition-colors"
                >
                  <FaTimes size={10} />
                </button>
              </div>
            </div>

            <h3 className="text-sm font-bold text-gray-800 tracking-tight leading-tight truncate">
              {notif.patientName}
            </h3>

            <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
              <span className="font-bold text-emerald-600 truncate inline-block max-w-[100px] align-bottom">{notif.meal}</span> submitted
            </p>

            <div className="mt-2.5 flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <FaCalendarDay className="text-emerald-500 text-[10px]" />
                <span className="text-[10px] font-bold text-gray-500 uppercase">{notif.dayTitle}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <FaClock className="text-emerald-500 text-[10px]" />
                <span className="text-[10px] font-bold text-gray-500">{notif.mealTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="mt-3 pt-3 border-t border-gray-100/80 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[9px] font-semibold text-gray-400 truncate max-w-[140px]">
            <FaClipboardList className="text-emerald-200" />
            <span className="truncate">{notif.chartTitle}</span>
          </div>
          <button
            onClick={handleReview}
            className="text-emerald-600 bg-transparent text-[10px] font-bold uppercase tracking-wide hover:text-emerald-700 transition-colors"
          >
            Review
          </button>
        </div>
      </div>
    </div>
  );
}

 